import { useState } from "react";
import { toast } from "sonner";
import { Check, Sparkles, Users, Loader2 } from "lucide-react";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LanguageContext";
import { formatPrice } from "../pages/Subscribe";

export const PLAN_FEATURES = {
  standard: ["Service reports", "Parts books", "Scanning", "PDF & Excel export", "Team management"],
  pro: ["Everything in Standard", "Planning", "Dashboard", "Machine checklists"],
};
export const PLAN_NAME = { standard: "Standard", pro: "Pro" };
export const planPrice = (b, plan) => (plan === "pro" ? b?.pro_seat_amount_cents : b?.seat_amount_cents);

export function PlanCards({ billing, value, onChange }) {
  const { t } = useLang();
  const cur = billing?.currency || "eur";
  return (
    <div className="grid grid-cols-2 gap-3" role="radiogroup" data-testid="plan-cards">
      {["standard", "pro"].map((p) => {
        const on = value === p;
        return (
          <button key={p} type="button" role="radio" aria-checked={on} onClick={() => onChange(p)} data-testid={`plan-card-${p}`}
            style={on ? { background: "rgba(242,103,34,0.06)" } : undefined}
            className={`text-left flex flex-col justify-start rounded-2xl p-4 border-2 transition-colors ${on ? "border-[#F26722]" : "border-zinc-800 bg-[#18181B] hover:border-zinc-700"}`}>
            <div className="flex items-center justify-between h-6">
              <span className="font-extrabold text-[#1A1A1A] flex items-center gap-1.5">{p === "pro" && <Sparkles size={15} className="text-[#F26722]" />}{PLAN_NAME[p]}</span>
              <span className="w-4 h-4 rounded-full border-2" style={{ borderColor: on ? "#F26722" : "#9CA3AF", background: on ? "#F26722" : "transparent" }} />
            </div>
            <p className="mt-2"><span className="text-2xl font-extrabold text-[#1A1A1A]" data-testid={`plan-card-${p}-price`}>{formatPrice(planPrice(billing, p), cur)}</span></p>
            <p className="text-[11px] text-zinc-500">{t("per user / month")}</p>
            <ul className="mt-3 space-y-1.5">
              {PLAN_FEATURES[p].map((f) => <li key={f} className="flex items-start gap-1.5 text-xs text-zinc-600"><Check size={13} className="text-[#F26722] shrink-0 mt-0.5" />{t(f)}</li>)}
            </ul>
          </button>
        );
      })}
    </div>
  );
}

export function CheckoutSummary({ billing, plan }) {
  const { t } = useLang();
  const cur = billing?.currency || "eur";
  const users = billing?.active_users ?? 0;
  const seat = planPrice(billing, plan) || 0;
  const total = (billing?.setup_fee_cents || 0) + seat * users;
  return (
    <div className="bg-[#18181B] border border-zinc-800 rounded-2xl p-4 mt-3" data-testid="checkout-summary">
      <div className="flex items-center justify-between text-sm text-zinc-600"><span>{t("One-time setup")}</span><span className="font-bold text-[#1A1A1A]" data-testid="subscribe-setup-fee">{formatPrice(billing?.setup_fee_cents, cur)}</span></div>
      <div className="flex items-center justify-between text-sm text-zinc-600 mt-1.5"><span className="flex items-center gap-1.5"><Users size={14} className="text-[#F26722]" />{PLAN_NAME[plan]}: {formatPrice(seat, cur)} × {users} {users === 1 ? t("user") : t("users")}</span><span className="font-bold text-[#1A1A1A]">{formatPrice(seat * users, cur)}</span></div>
      <div className="border-t border-zinc-800 mt-3 pt-3 flex items-center justify-between">
        <span className="text-sm font-semibold text-zinc-500">{t("Estimated first payment")}</span>
        <span className="text-2xl font-extrabold text-[#1A1A1A]" data-testid="subscribe-first-payment">{formatPrice(total, cur)}</span>
      </div>
    </div>
  );
}

export function useCheckout() {
  const [loading, setLoading] = useState(false);
  const start = async (plan) => {
    setLoading(true);
    try {
      const { data } = await api.post("/billing/checkout", { plan, success_url: `${window.location.origin}/billing-success`, cancel_url: `${window.location.origin}/billing-cancel` });
      if (data?.url) window.location.assign(data.url);
      else toast.error("Could not start checkout");
    } catch (e) {
      const code = e?.response?.data?.detail?.code;
      if (code === "NDA_SIGNATURE_REQUIRED" || code === "SUBSCRIPTION_REQUIRED") return; // interceptor routes
      toast.error(apiError(e));
    } finally { setLoading(false); }
  };
  return { loading, start };
}

// Active subscription: show the current plan and one switch button (billing owner only).
export function ActivePlan({ billing, setBilling, onNeedCheckout }) {
  const { t } = useLang();
  const { refresh } = useAuth();
  const [busy, setBusy] = useState(false);
  const current = billing.plan === "pro" ? "pro" : "standard";
  const target = current === "pro" ? "standard" : "pro";
  const change = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/billing/plan", { plan: target });
      setBilling(data);
      if (refresh) await refresh();
      toast.success(target === "pro" ? t("You're now on Pro") : t("You're now on Standard"));
    } catch (e) {
      const st = e?.response?.status;
      if (st === 409) onNeedCheckout();
      else if (st === 403) toast.error(t("Only the billing owner can change the plan."));
      else toast.error(apiError(e));
    } finally { setBusy(false); }
  };
  return (
    <div className="mt-3 border-t border-zinc-800 pt-3" data-testid="active-plan">
      {billing.can_manage === true && !billing.pro_granted && (
        <>
          <button onClick={change} disabled={busy} data-testid="switch-plan-btn"
            className={`w-full font-bold py-2.5 rounded-xl flex items-center justify-center gap-2 disabled:opacity-60 transition-colors ${target === "pro" ? "bg-[#F26722] text-white hover:bg-[#D95513]" : "bg-[#18181B] border border-zinc-700 text-zinc-200 hover:border-zinc-500"}`}>
            {busy ? <Loader2 className="animate-spin" size={16} /> : target === "pro" ? <><Sparkles size={16} /> {t("Upgrade to Pro")} · {formatPrice(billing.pro_seat_amount_cents, billing.currency)} / {t("user")}</> : t("Switch back to Standard")}
          </button>
          <p className="text-xs text-zinc-500 mt-2" data-testid="plan-prorata-note">{t("Plan changes are charged or credited pro rata on your next invoice right away.")}</p>
        </>
      )}
    </div>
  );
}
